import type { Session, SupabaseClient, User } from '@supabase/supabase-js';
import type { AccountRuntimeConfig, AuthState } from './types.ts';

type AuthListener = (state: AuthState) => void;

/**
 * The Supabase client is loaded only when accounts are enabled. Guest visitors
 * therefore keep the existing static-first path and do not download an auth
 * SDK they never asked to use.
 */
export class AccountAuth {
  private client: SupabaseClient | null = null;
  private runtime: AccountRuntimeConfig | null = null;
  private state: AuthState = { status: 'loading' };
  private listeners = new Set<AuthListener>();
  private initialized = false;

  getState(): AuthState {
    return this.state;
  }

  getClient(): SupabaseClient | null {
    return this.client;
  }

  subscribe(listener: AuthListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  async initialize(runtime: AccountRuntimeConfig): Promise<AuthState> {
    if (this.initialized) return this.state;
    this.initialized = true;
    this.runtime = runtime;

    if (!runtime.enabled || !runtime.supabase_url || !runtime.supabase_publishable_key) {
      this.setState({ status: 'disabled' });
      return this.state;
    }

    try {
      const { createClient } = await import('@supabase/supabase-js');
      this.client = createClient(runtime.supabase_url, runtime.supabase_publishable_key, {
        auth: {
          flowType: 'pkce',
          persistSession: true,
          autoRefreshToken: true,
          // exchangeCallbackCode owns the PKCE callback exchange below. Do
          // not let GoTrue consume the code during client initialization too.
          detectSessionInUrl: false,
          storageKey: 'draftpuppy-auth',
        },
      });

      await this.exchangeCallbackCode();
      const { data, error } = await this.client.auth.getSession();
      if (error) throw error;
      this.setState(stateFromSession(data.session));

      this.client.auth.onAuthStateChange((_event, session) => {
        this.setState(stateFromSession(session));
      });
    } catch (error) {
      this.setState({
        status: 'signed-out',
        error: error instanceof Error ? 'Account service is unavailable.' : 'Account setup failed.',
      });
    }
    return this.state;
  }

  async sendMagicLink(email: string, captchaToken?: string | null): Promise<void> {
    if (!this.client || !this.runtime?.production_origin) throw new Error('Accounts are not available right now.');
   const normalized = email.trim().toLowerCase();
   if (!/^\S+@\S+\.\S+$/.test(normalized)) throw new Error('Enter a valid email address.');
    const redirectOrigin = typeof location !== 'undefined' ? location.origin : this.runtime.production_origin;
    if (!redirectOrigin) throw new Error('Accounts are not available right now.');

   const { error } = await this.client.auth.signInWithOtp({
     email: normalized,
     options: {
       shouldCreateUser: true,
        emailRedirectTo: `${redirectOrigin}/auth/callback`,
       ...(captchaToken ? { captchaToken } : {}),
     },
   });
    if (error) throw new Error('Could not send the sign-in link. Please try again later.');
  }

  async signOut(): Promise<void> {
    if (!this.client) return;
    const { error } = await this.client.auth.signOut();
    if (error) throw error;
  }

  async deleteAccount(): Promise<void> {
    if (!this.client) throw new Error('Accounts are not available right now.');
    const { data } = await this.client.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Your sign-in has expired. Please sign in again.');

    const response = await fetch('/api/account', {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
    });
   if (!response.ok) throw new Error('Could not delete the account. Please try again.');
    const { error } = await this.client.auth.signOut({ scope: 'local' });
    if (error) throw new Error('Account data was deleted, but this device could not finish signing out. Please clear site data and reload.');
  }

  private async exchangeCallbackCode(): Promise<void> {
    if (!this.client || typeof location === 'undefined') return;
    const code = new URLSearchParams(location.search).get('code');
    if (!code) return;

    const { error } = await this.client.auth.exchangeCodeForSession(code);
    if (error) throw error;
    history.replaceState({}, document.title, `${location.pathname}${location.hash}`);
  }

  private setState(next: AuthState): void {
    this.state = next;
    for (const listener of this.listeners) listener(next);
  }
}

function stateFromSession(session: Session | null): AuthState {
  if (!session?.user) return { status: 'signed-out' };
  return signedInState(session.user);
}

function signedInState(user: User): AuthState {
  return { status: 'signed-in', user_id: user.id, email: user.email ?? null };
}
